/* crm-goals-territory.jsx — Phase 5: Goal Tracking (24), Territory Management (25), Sales Playbooks (26)
   Bridged component — no imports, all via window globals. */

const { useState, useEffect, useMemo, useCallback, useRef } = React;

/* ── helpers ── */
const _date = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const card = { background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 10 };
const inputSx = { padding: '6px 10px', border: '1px solid var(--nx-border)', borderRadius: 6, background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 12, width: '100%', boxSizing: 'border-box' };
const btnPrimary = { padding: '6px 14px', borderRadius: 6, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const btnGhost = { padding: '6px 12px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'transparent', color: 'var(--nx-text)', fontSize: 12, cursor: 'pointer' };

const METRIC_TYPES  = ['REVENUE', 'DEALS_CLOSED', 'CALLS_MADE', 'EMAILS_SENT', 'MEETINGS_HELD', 'PIPELINE_VALUE', 'WIN_RATE'];
const PERIODS       = ['Q1', 'Q2', 'Q3', 'Q4', 'Annual'];
const STEP_TYPES    = ['CALL', 'EMAIL', 'DEMO', 'PROPOSAL', 'CLOSE'];
const STEP_ICONS    = { CALL: 'Phone', EMAIL: 'Mail', DEMO: 'Monitor', PROPOSAL: 'FileText', CLOSE: 'CheckCircle' };
const STATUS_COLORS = { ON_TRACK: '#10b981', AT_RISK: '#f59e0b', BEHIND: '#ef4444', COMPLETED: 'var(--nx-accent)' };

/* ── StatusPill ── */
const StatusPill = ({ status }) => {
  const color = STATUS_COLORS[status] || '#6b7280';
  return <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: color + '20', color }}>{status}</span>;
};

/* ══════════════════════════════════════════════════════
   GoalProgressBar
   ══════════════════════════════════════════════════════ */
const GoalProgressBar = ({ pct = 0, color = 'var(--nx-accent)' }) => {
  const safe = Math.min(100, Math.max(0, pct));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: 'var(--nx-border)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${safe}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.4s' }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color, minWidth: 32, textAlign: 'right' }}>{Math.round(safe)}%</span>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   GoalCheckInModal
   ══════════════════════════════════════════════════════ */
const GoalCheckInModal = ({ goal, onClose, onCheckedIn }) => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast();
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!value) { toast.show({ title: 'Enter a value', type: 'error' }); return; }
    setSaving(true);
    try {
      await window.__nx_api?.goals?.checkIn(wsId, goal.id, { value: Number(value), note });
      toast.show({ title: 'Check-in recorded', type: 'success' });
      onCheckedIn?.();
      onClose();
    } catch (e) { toast.show({ title: e.message, type: 'error' }); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ ...card, width: 380, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: 14 }}>Check In: {goal.name}</span>
          <button onClick={onClose} style={{ ...btnGhost, padding: '4px 8px' }}><NexusIcon name="X" size={12} /></button>
        </div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 4 }}>Current Value ({goal.unit || 'units'})</label>
          <input type="number" style={inputSx} value={value} onChange={e => setValue(e.target.value)} placeholder="Enter current value" /></div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 4 }}>Note (optional)</label>
          <textarea style={{ ...inputSx, resize: 'vertical', minHeight: 60 }} value={note} onChange={e => setNote(e.target.value)} placeholder="What's driving this progress?" /></div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button style={btnGhost} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Submit'}</button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   GoalCard
   ══════════════════════════════════════════════════════ */
const GoalCard = ({ goal, onCheckIn }) => {
  const pct = goal.targetValue ? Math.round((Number(goal.currentValue || 0) / Number(goal.targetValue)) * 100) : 0;
  const color = STATUS_COLORS[goal.status] || 'var(--nx-accent)';
  return (
    <div style={{ ...card, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{goal.name}</div>
          <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{goal.metricType} · {goal.period}</div>
        </div>
        <StatusPill status={goal.status || 'ON_TRACK'} />
      </div>
      <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
        <div><span style={{ color: 'var(--nx-text-muted)' }}>Current: </span><strong>{Number(goal.currentValue || 0).toLocaleString()} {goal.unit}</strong></div>
        <div><span style={{ color: 'var(--nx-text-muted)' }}>Target: </span><strong>{Number(goal.targetValue || 0).toLocaleString()} {goal.unit}</strong></div>
      </div>
      <GoalProgressBar pct={pct} color={color} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{_date(goal.startDate)} – {_date(goal.endDate)}</span>
        <button style={{ ...btnGhost, padding: '4px 10px', fontSize: 11 }} onClick={() => onCheckIn?.(goal)}>Check In</button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   GoalForm
   ══════════════════════════════════════════════════════ */
const GoalForm = ({ onSave, onCancel }) => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast();
  const [f, setF] = useState({ name: '', metricType: 'REVENUE', target: '', unit: '', period: 'Q1', startDate: '', endDate: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!wsId) { toast.show({ title: 'Workspace not loaded', type: 'error' }); return; }
    if (!f.name.trim() || !f.target) { toast.show({ title: 'Name and target are required', type: 'error' }); return; }
    setSaving(true);
    try {
      await window.__nx_api?.goals?.create(wsId, { ...f, target: Number(f.target) });
      toast.show({ title: 'Goal created', type: 'success' });
      onSave?.();
    } catch (e) { toast.show({ title: e.message, type: 'error' }); }
    setSaving(false);
  };

  return (
    <div style={{ ...card, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontWeight: 700, fontSize: 13 }}>New Goal</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Name</label><input style={inputSx} value={f.name} onChange={e => set('name', e.target.value)} /></div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Metric Type</label>
          <select style={inputSx} value={f.metricType} onChange={e => set('metricType', e.target.value)}>{METRIC_TYPES.map(m => <option key={m} value={m}>{m}</option>)}</select></div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Target</label><input type="number" style={inputSx} value={f.target} onChange={e => set('target', e.target.value)} /></div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Unit</label><input style={inputSx} value={f.unit} placeholder="e.g. USD, calls" onChange={e => set('unit', e.target.value)} /></div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Period</label>
          <select style={inputSx} value={f.period} onChange={e => set('period', e.target.value)}>{PERIODS.map(p => <option key={p} value={p}>{p}</option>)}</select></div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Start Date</label><input type="date" style={inputSx} value={f.startDate} onChange={e => set('startDate', e.target.value)} /></div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>End Date</label><input type="date" style={inputSx} value={f.endDate} onChange={e => set('endDate', e.target.value)} /></div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        {onCancel && <button style={btnGhost} onClick={onCancel}>Cancel</button>}
        <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Creating...' : 'Create Goal'}</button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   GoalsView
   ══════════════════════════════════════════════════════ */
const GoalsView = () => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [checkInGoal, setCheckInGoal] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    window.__nx_api?.goals?.list(wsId).then(r => setGoals(Array.isArray(r) ? r : r?.data || [])).catch(() => setGoals([])).finally(() => setLoading(false));
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  const filtered = statusFilter ? goals.filter(g => g.status === statusFilter) : goals;

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>Goals</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <select style={{ ...inputSx, width: 130 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button style={btnPrimary} onClick={() => setShowForm(s => !s)}>+ New Goal</button>
        </div>
      </div>
      {showForm && <GoalForm onSave={() => { setShowForm(false); load(); }} onCancel={() => setShowForm(false)} />}
      {loading ? <div style={{ color: 'var(--nx-text-muted)', fontSize: 13 }}>Loading...</div> : (
        filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--nx-text-muted)' }}>No goals found</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
            {filtered.map(g => <GoalCard key={g.id} goal={g} onCheckIn={setCheckInGoal} />)}
          </div>
        )
      )}
      {checkInGoal && <GoalCheckInModal goal={checkInGoal} onClose={() => setCheckInGoal(null)} onCheckedIn={load} />}
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   TerritoryTree
   ══════════════════════════════════════════════════════ */
// H-11: visited Set prevents infinite recursion on circular parent-child references
const TerritoryTree = ({ items = [], level = 0, _visited = new Set() }) => (
  <div>
    {items.map(t => {
      if (!t.id || _visited.has(t.id)) return null;
      const nextVisited = new Set(_visited);
      nextVisited.add(t.id);
      return (
        <div key={t.id}>
          <div style={{ paddingLeft: level * 20, padding: `6px 8px 6px ${8 + level * 20}px`, display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1px solid var(--nx-border)' }}>
            <NexusIcon name="MapPin" size={12} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
            <span style={{ fontSize: 12, fontWeight: 600 }}>{t.name}</span>
            {t.manager && <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>· {t.manager}</span>}
            {t.active !== false && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 10, background: '#10b98120', color: '#10b981' }}>Active</span>}
          </div>
          {t.children?.length > 0 && <TerritoryTree items={t.children} level={level + 1} _visited={nextVisited} />}
        </div>
      );
    })}
  </div>
);

/* ══════════════════════════════════════════════════════
   TerritoryCard
   ══════════════════════════════════════════════════════ */
const TerritoryCard = ({ territory, onEdit }) => (
  <div style={{ ...card, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{territory.name}</div>
        {territory.manager && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>Manager: {territory.manager}</div>}
      </div>
      {territory.active !== false && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#10b98120', color: '#10b981' }}>Active</span>}
    </div>
    <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
      <span><NexusIcon name="Users" size={11} style={{ marginRight: 3 }} />{territory.memberCount || 0} members</span>
      <span><NexusIcon name="Filter" size={11} style={{ marginRight: 3 }} />{(territory.rules || []).length} rules</span>
    </div>
    <button style={{ ...btnGhost, padding: '4px 10px', fontSize: 11, alignSelf: 'flex-start' }} onClick={() => onEdit?.(territory)}>Edit</button>
  </div>
);

/* ══════════════════════════════════════════════════════
   TerritoryForm
   ══════════════════════════════════════════════════════ */
const TerritoryForm = ({ onSave, onCancel }) => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast();
  const [name, setName] = useState('');
  const [manager, setManager] = useState('');
  const [rules, setRules] = useState([{ field: '', operator: 'equals', value: '' }]);
  const [saving, setSaving] = useState(false);

  const addRule = () => setRules(r => [...r, { field: '', operator: 'equals', value: '' }]);
  const removeRule = (i) => setRules(r => r.filter((_, idx) => idx !== i));
  const updateRule = (i, k, v) => setRules(r => r.map((x, idx) => idx === i ? { ...x, [k]: v } : x));

  const submit = async () => {
    if (!wsId) { toast.show({ title: 'Workspace not loaded', type: 'error' }); return; }
    if (!name.trim()) { toast.show({ title: 'Name is required', type: 'error' }); return; }
    setSaving(true);
    try {
      await window.__nx_api?.territories?.create(wsId, { name, manager, rules });
      toast.show({ title: 'Territory created', type: 'success' });
      onSave?.();
    } catch (e) { toast.show({ title: e.message, type: 'error' }); }
    setSaving(false);
  };

  return (
    <div style={{ ...card, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontWeight: 700, fontSize: 13 }}>New Territory</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Name</label><input style={inputSx} value={name} onChange={e => setName(e.target.value)} /></div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Manager</label><input style={inputSx} value={manager} onChange={e => setManager(e.target.value)} placeholder="Name or email" /></div>
      </div>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)' }}>RULES</span>
          <button style={{ ...btnGhost, padding: '3px 10px', fontSize: 11 }} onClick={addRule}>+ Add</button>
        </div>
        {rules.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input style={{ ...inputSx, flex: 1 }} placeholder="Field" value={r.field} onChange={e => updateRule(i, 'field', e.target.value)} />
            <select style={{ ...inputSx, width: 110 }} value={r.operator} onChange={e => updateRule(i, 'operator', e.target.value)}>
              {['equals','contains','starts_with','in_region'].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <input style={{ ...inputSx, flex: 1 }} placeholder="Value" value={r.value} onChange={e => updateRule(i, 'value', e.target.value)} />
            <button style={{ ...btnGhost, padding: '4px 8px', color: '#ef4444' }} onClick={() => removeRule(i)}><NexusIcon name="X" size={12} /></button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        {onCancel && <button style={btnGhost} onClick={onCancel}>Cancel</button>}
        <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Creating...' : 'Create Territory'}</button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   TerritoriesView
   ══════════════════════════════════════════════════════ */
const TerritoriesView = () => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const [territories, setTerritories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showTree, setShowTree] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    window.__nx_api?.territories?.list(wsId).then(r => setTerritories(Array.isArray(r) ? r : r?.data || [])).catch(() => setTerritories([])).finally(() => setLoading(false));
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>Territories</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btnGhost} onClick={() => setShowTree(t => !t)}><NexusIcon name="GitBranch" size={12} style={{ marginRight: 4 }} />{showTree ? 'Grid View' : 'Tree View'}</button>
          <button style={btnPrimary} onClick={() => setShowForm(s => !s)}>+ New Territory</button>
        </div>
      </div>
      {showForm && <TerritoryForm onSave={() => { setShowForm(false); load(); }} onCancel={() => setShowForm(false)} />}
      {loading ? <div style={{ color: 'var(--nx-text-muted)', fontSize: 13 }}>Loading...</div> : territories.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--nx-text-muted)' }}>No territories defined</div>
      ) : showTree ? (
        <div style={card}><TerritoryTree items={territories} /></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {territories.map(t => <TerritoryCard key={t.id} territory={t} />)}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   PlaybookStepItem
   ══════════════════════════════════════════════════════ */
const PlaybookStepItem = ({ step, index }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderBottom: '1px solid var(--nx-border)' }}>
    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--nx-accent-tint-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-accent)' }}>{index + 1}</span>
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
        <NexusIcon name={STEP_ICONS[step.stepType] || 'Circle'} size={12} style={{ color: 'var(--nx-accent)' }} />
        <span style={{ fontSize: 12, fontWeight: 700 }}>{step.title}</span>
        {step.required && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 10, background: '#ef444420', color: '#ef4444' }}>Required</span>}
      </div>
      {step.description && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{step.description}</div>}
    </div>
    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: 'var(--nx-border)', color: 'var(--nx-text-muted)', fontWeight: 600 }}>{step.stepType}</span>
  </div>
);

/* ══════════════════════════════════════════════════════
   PlaybookCard
   ══════════════════════════════════════════════════════ */
const PlaybookCard = ({ playbook, onView }) => (
  <div style={{ ...card, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 3 }}>{playbook.name}</div>
        <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{playbook.industry || 'General'} · {playbook.targetStage || 'All stages'}</div>
      </div>
      {playbook.active !== false && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#10b98120', color: '#10b981' }}>Active</span>}
    </div>
    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{(playbook.steps || []).length} steps</div>
    <button style={{ ...btnPrimary, padding: '5px 10px', fontSize: 11, alignSelf: 'flex-start' }} onClick={() => onView?.(playbook)}>View Steps</button>
  </div>
);

/* ══════════════════════════════════════════════════════
   PlaybookDetailView
   ══════════════════════════════════════════════════════ */
const PlaybookDetailView = ({ playbook, onBack }) => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast();
  const [steps, setSteps] = useState(playbook.steps || []);
  const [showAdd, setShowAdd] = useState(false);
  const [newStep, setNewStep] = useState({ type: 'CALL', title: '', description: '', required: false });
  const [saving, setSaving] = useState(false);

  const addStep = async () => {
    if (!wsId) { toast.show({ title: 'Workspace not loaded', type: 'error' }); return; }
    if (!newStep.title) { toast.show({ title: 'Step title required', type: 'error' }); return; }
    setSaving(true);
    try {
      const s = await window.__nx_api?.playbooks?.addStep(wsId, playbook.id, newStep);
      setSteps(prev => [...prev, s || { ...newStep, id: Date.now() }]);
      setNewStep({ type: 'CALL', title: '', description: '', required: false });
      setShowAdd(false);
      toast.show({ title: 'Step added', type: 'success' });
    } catch (e) { toast.show({ title: e.message, type: 'error' }); }
    setSaving(false);
  };

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button style={{ ...btnGhost, padding: '4px 10px', fontSize: 11 }} onClick={onBack}><NexusIcon name="ChevronLeft" size={12} /> Back</button>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{playbook.name}</span>
      </div>
      <div style={card}>
        {steps.map((s, i) => <PlaybookStepItem key={s.id || i} step={s} index={i} />)}
        {!steps.length && <div style={{ padding: 20, textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 12 }}>No steps yet</div>}
      </div>
      {showAdd ? (
        <div style={{ ...card, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 12 }}>Add Step</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <select style={inputSx} value={newStep.type} onChange={e => setNewStep(s => ({ ...s, type: e.target.value }))}>
              {STEP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input style={inputSx} placeholder="Step title" value={newStep.title} onChange={e => setNewStep(s => ({ ...s, title: e.target.value }))} />
          </div>
          <input style={inputSx} placeholder="Description (optional)" value={newStep.description} onChange={e => setNewStep(s => ({ ...s, description: e.target.value }))} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={newStep.required} onChange={e => setNewStep(s => ({ ...s, required: e.target.checked }))} /> Required step
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btnGhost} onClick={() => setShowAdd(false)}>Cancel</button>
            <button style={btnPrimary} onClick={addStep} disabled={saving}>{saving ? 'Adding...' : 'Add Step'}</button>
          </div>
        </div>
      ) : (
        <button style={{ ...btnGhost, alignSelf: 'flex-start' }} onClick={() => setShowAdd(true)}>+ Add Step</button>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   PlaybooksView
   ══════════════════════════════════════════════════════ */
const PlaybooksView = () => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast();
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [newPB, setNewPB] = useState({ name: '', industry: '', targetStage: '' });
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    window.__nx_api?.playbooks?.list(wsId).then(r => setPlaybooks(Array.isArray(r) ? r : r?.data || [])).catch(() => setPlaybooks([])).finally(() => setLoading(false));
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  if (selected) return <PlaybookDetailView playbook={selected} onBack={() => setSelected(null)} />;

  const create = async () => {
    if (!newPB.name) { toast.show({ title: 'Name required', type: 'error' }); return; }
    setCreating(true);
    try {
      await window.__nx_api?.playbooks?.create(wsId, newPB);
      toast.show({ title: 'Playbook created', type: 'success' });
      setShowForm(false); setNewPB({ name: '', industry: '', targetStage: '' }); load();
    } catch (e) { toast.show({ title: e.message, type: 'error' }); }
    setCreating(false);
  };

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>Sales Playbooks</span>
        <button style={btnPrimary} onClick={() => setShowForm(s => !s)}>+ New Playbook</button>
      </div>
      {showForm && (
        <div style={{ ...card, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <input style={inputSx} placeholder="Playbook name *" value={newPB.name} onChange={e => setNewPB(p => ({ ...p, name: e.target.value }))} />
            <input style={inputSx} placeholder="Industry" value={newPB.industry} onChange={e => setNewPB(p => ({ ...p, industry: e.target.value }))} />
            <input style={inputSx} placeholder="Target stage" value={newPB.targetStage} onChange={e => setNewPB(p => ({ ...p, targetStage: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btnGhost} onClick={() => setShowForm(false)}>Cancel</button>
            <button style={btnPrimary} onClick={create} disabled={creating}>{creating ? 'Creating...' : 'Create'}</button>
          </div>
        </div>
      )}
      {loading ? <div style={{ color: 'var(--nx-text-muted)', fontSize: 13 }}>Loading...</div> : playbooks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--nx-text-muted)' }}>No playbooks yet</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {playbooks.map(p => <PlaybookCard key={p.id} playbook={p} onView={setSelected} />)}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   GoalsTerritoryView — 3-tab container
   ══════════════════════════════════════════════════════ */
const GoalsTerritoryView = () => {
  const [tab, setTab] = useState('goals');
  const tabs = [{ id: 'goals', label: 'Goals', icon: 'Target' }, { id: 'territories', label: 'Territories', icon: 'MapPin' }, { id: 'playbooks', label: 'Playbooks', icon: 'BookOpen' }];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', borderBottom: '1px solid var(--nx-border)', paddingLeft: 20 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: tab === t.id ? 'var(--nx-accent)' : 'var(--nx-text-muted)', borderBottom: tab === t.id ? '2px solid var(--nx-accent)' : '2px solid transparent', marginBottom: -1 }}>
            <NexusIcon name={t.icon} size={13} /> {t.label}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {tab === 'goals' && <GoalsView />}
        {tab === 'territories' && <TerritoriesView />}
        {tab === 'playbooks' && <PlaybooksView />}
      </div>
    </div>
  );
};

/* ── window registrations ── */
window.GoalsView          = GoalsView;
window.TerritoriesView    = TerritoriesView;
window.PlaybooksView      = PlaybooksView;
window.GoalsTerritoryView = GoalsTerritoryView;
